/**
 * The one path that adds something to a deck.
 *
 * Both `+` buttons land here — the app header's and the directory panel's — because they are two
 * vocabularies for the same five actions, not two features. The header speaks `AddItem`, the panel
 * speaks `DirectoryAddItem`, and mapping between them is the container's job; neither component
 * should learn the other's union.
 *
 * Two of the five go through an endpoint that already exists:
 *   document  POST /documents-api/<conversationId>
 *   image     POST /conversation-images-api/<conversationId>          (file picker first)
 *   figma     out of scope this pass
 *
 * An artifact is the exception: it creates an EMPTY slide row and nothing more. Generation is the
 * artifact agent's job and it is not plugged in yet, so a `+ artifact` that produced a plausible-
 * looking slide would be lying about what the button did.
 *
 * A WEBSITE IS NOW THE SAME KIND OF EXCEPTION. It used to stop the whole create on a modal asking for
 * a URL, which is a create that cannot finish until you have already decided — so `+ website` opened a
 * box instead of adding a website. It now mints a BLANK card (`status: 'empty'`) and hands its key
 * back immediately; the row appears selected, and the preview draws the intake that asks for the
 * address (`preview/WebsiteIntake`). The fetch that used to live here moved with the question, to
 * `useWebActions.resolve` — one card, patched in place, rather than a card that only exists if the
 * fetch got as far as making one. Same shape the canvas has always had (`addEmptyWeb`).
 *
 * TWO STORES, ONE ITEM. A document and an image both live in a backend POOL — the Library row, the
 * conversation's image pool — and the canvas blob holds a CARD that references it (the
 * originals-vs-instances rule; see `useBirtvyDocLibrarySync`). Creating one therefore means both: POST
 * the original, then place the instance through `mutate`. Doing only the POST would put the thing in
 * the Library and leave the directory empty, which reads as "the button did nothing".
 *
 * WHEN THE BLOB IS READ-ONLY. `mutate` is a documented no-op for a deck that has never been opened on
 * the canvas (see `useDeckBlob`), and a no-op never calls the change function. Every create below mints
 * its id INSIDE that function, so `minted.id` stays null and `create` honestly returns null instead of
 * a row key for something that was never written.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DirectoryAddItem } from '~/design-system/components/v1_5/Directory';
import { DOC_H, DOC_W, type BirtvyDoc } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import { imageInsertSize, type BirtvyImage } from '~/design-system/components/DeckGen/birtvy/birtvyImage';
import { SLIDE_H, SLIDE_W, type BirtvySlide } from '~/design-system/components/DeckGen/birtvy/birtvySlide';
import { blankSlideHtml } from '~/design-system/components/DeckGen/birtvyOpen/birtvyOpenDoc';
import { createDocument as createLibraryDocument } from '~/design-system/components/DeckGen/birtvy/birtvyDocSync';
import {
  WEB_CARD_DEFAULT_SIZE,
  type BirtvyWeb,
} from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';
import {
  ensureVisionCompatibleImageFile,
  HEIC_NOT_SUPPORTED,
  isHeicFile,
  measureImageUrl,
} from '~/design-system/components/DeckGen/utils/imageElement';
import { useToast } from '~/contexts/toast-context';
import {
  findCanvasPlacement,
  unionBounds,
  type PlacementRect,
} from '~/design-system/components/DeckGen/utils/canvasPlacement';
import { CANVAS_SNAP_GAP } from '~/design-system/components/DeckGen/utils/canvasSnap';
import type { DeckBlob } from '../deckContent';
import { itemKey } from '../ids';
import type { DeckBlobState } from '../useDeckBlob';
import { nextBlobId } from './toDirectoryItems';
import { fetchWebPreview } from './webPreview';
import { libraryQueryKey } from '../chat/useWorkspaceResources';

export interface DeckCreate {
  /** Resolves to the new row key (`slide:9`), or null if nothing was made — cancelled, or unsupported. */
  create: (item: DirectoryAddItem) => Promise<string | null>;
  /**
   * A website, from the address the preview's intake collected.
   *
   * Separate from `create` because it is the only add that needs an ARGUMENT: every other kind can be
   * made from the button alone, and widening `create` to `(item, arg?)` would put an optional string
   * on four calls that can never use it.
   */
  createWebsite: (url: string) => Promise<string | null>;
  /** A create is in flight. The `+` shows it on itself rather than in a toast. */
  creating: boolean;
}

/** A mutable box for the id the change function mints, so TS does not narrow it away across the await. */
type Minted = { id: number | null };

// ── Placement ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Every item in the blob as a world rect. The obstacle set a new card must not land on.
 *
 * Slides are always SLIDE_W x SLIDE_H — a `BirtvySlide` stores only its top-left, because a slide is
 * one fixed size. Notes ride the index signature and are shape-checked rather than trusted.
 */
function obstaclesOf(blob: DeckBlob): PlacementRect[] {
  const rects: PlacementRect[] = [];
  for (const s of blob.slides ?? []) rects.push({ x: s.x, y: s.y, width: SLIDE_W, height: SLIDE_H });
  const boxed = [blob.docs, blob.images, blob.webs, blob.notes];
  for (const rows of boxed) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows as { x?: unknown; y?: unknown; width?: unknown; height?: unknown }[]) {
      if (
        typeof row?.x === 'number' &&
        typeof row.y === 'number' &&
        typeof row.width === 'number' &&
        typeof row.height === 'number'
      ) {
        rects.push({ x: row.x, y: row.y, width: row.width, height: row.height });
      }
    }
  }
  return rects;
}

/**
 * Where a new card of `size` goes: just right of everything the deck already holds, pushed further
 * right by `findCanvasPlacement` if that spot is taken.
 *
 * The canvas itself would drop it at the viewport centre, but there is no viewport here — this
 * workspace is a list, not a camera. Landing past the right edge of the content is the one rule that
 * needs no camera and never buries an existing item. `extra` is the obstacle list of cards created
 * earlier in the SAME `mutate` (a multi-file image add), which are not in the blob yet.
 */
export function placeIn(
  blob: DeckBlob,
  size: { width: number; height: number },
  extra: PlacementRect[] = [],
): { x: number; y: number } {
  const obstacles = [...obstaclesOf(blob), ...extra];
  if (obstacles.length === 0) return { x: 0, y: 0 };
  const bounds = unionBounds(obstacles);
  const centre = {
    x: bounds.x + bounds.width + CANVAS_SNAP_GAP + size.width / 2,
    y: bounds.y + size.height / 2,
  };
  const placed = findCanvasPlacement(centre, size, obstacles);
  return { x: Math.round(placed.x - size.width / 2), y: Math.round(placed.y - size.height / 2) };
}

// ── Pickers ──────────────────────────────────────────────────────────────────────────────────────────

/**
 * The OS file picker, as a promise.
 *
 * Resolves `[]` on cancel via the input's `cancel` event (Chrome 113+, Safari 16.4+, Firefox 109+). On
 * anything older a cancelled picker fires nothing at all and this promise never settles, which would
 * leave the `+` spinning — the reason `creating` is worth watching if that ever shows up.
 */
function pickImageFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve([]);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.style.display = 'none';
    const settle = (files: File[]) => {
      input.remove();
      resolve(files);
    };
    input.addEventListener('change', () => settle(input.files ? Array.from(input.files) : []));
    input.addEventListener('cancel', () => settle([]));
    document.body.appendChild(input);
    input.click();
  });
}

// ── The five creates ─────────────────────────────────────────────────────────────────────────────────

/**
 * A blank artifact, exactly as the canvas makes one.
 *
 * `blankSlideHtml()` is a real but empty artifact document, and it is what makes the row EXIST: this
 * workspace defines an artifact as a slide with `docHtml` set, so a slide without it lands in the blob
 * and shows up in no directory at all — the `+` would write a row nobody can see. `BirtvyCanvas` line
 * 1482 does the same thing for the same reason, down to `status: 'done'`.
 *
 * Blank is not the same as generated. There is no content here and none is implied; the artifact agent
 * fills it in a later pass, and `useBirtvyDocGeneration` already recognises this exact string as "still
 * blank" when it does.
 */
async function createArtifact(mutate: DeckBlobState['mutate']): Promise<number | null> {
  const minted: Minted = { id: null };
  await mutate((blob) => {
    const id = nextBlobId(blob);
    const { x, y } = placeIn(blob, { width: SLIDE_W, height: SLIDE_H });
    const slide: BirtvySlide = { id, x, y, status: 'done', prompt: '', docHtml: blankSlideHtml() };
    minted.id = id;
    return { ...blob, slides: [...(blob.slides ?? []), slide] };
  });
  return minted.id;
}

/** A Library ORIGINAL, then the card that references it. Fails closed: no row, no card. */
async function createDocumentCard(
  conversationId: string,
  mutate: DeckBlobState['mutate'],
): Promise<number | null> {
  // `title: undefined` lets the backend derive it, the same rule every other canvas write follows —
  // the canvas must never send a title of its own or the first edit silently renames the document.
  const row = await createLibraryDocument(conversationId, { content: '' });
  if (!row) return null;

  const minted: Minted = { id: null };
  await mutate((blob) => {
    const id = nextBlobId(blob);
    const { x, y } = placeIn(blob, { width: DOC_W, height: DOC_H });
    const doc: BirtvyDoc = {
      id,
      x,
      y,
      width: DOC_W,
      height: DOC_H,
      content: row.content ?? '',
      title: row.title ?? '',
      documentId: row.id,
      groupId: null,
    };
    minted.id = id;
    return { ...blob, docs: [...(blob.docs ?? []), doc] };
  });
  return minted.id;
}

/** One picture in the conversation's pool, as its upload answered. `id` is the content hash. */
export interface PoolImage {
  id: string;
  url: string;
  name: string;
  description: string;
}

/**
 * One file into the conversation's image pool. Null when the pool refused it: the backend answers a file it
 * will not take (wrong format, over 10 MB) with a 500, so a status is all there is to go on.
 *
 * The file goes as it is: transcoding what the vision analysis cannot read (AVIF, HEIC) is the caller's
 * step, because the caller may also need those same bytes (a picture added by hand shows them at once).
 */
export async function uploadPoolImage(conversationId: string, file: File): Promise<PoolImage | null> {
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`/conversation-images-api/${encodeURIComponent(conversationId)}`, {
    method: 'POST',
    body,
  });
  if (!res.ok) return null;
  const data = (await res.json()) as Partial<PoolImage>;
  if (!data.url) return null;
  return {
    id: typeof data.id === 'string' ? data.id : '',
    url: data.url,
    name: typeof data.name === 'string' ? data.name : file.name,
    description: typeof data.description === 'string' ? data.description : '',
  };
}

/** A card to place: the pool URL, the card's box on the canvas, and what to call it. */
export interface ImageCardInput {
  src: string;
  width: number;
  height: number;
  name: string;
}

/**
 * Cards for pictures already in the pool, in ONE `mutate`, so a five-image add is one blob write rather
 * than five racing ones. With `reuse`, a picture the Directory already shows (same `src`) keeps its card
 * instead of gaining a second: the pool itself stores a file once, and a picture added to an artifact twice
 * is one picture. Resolves to the card id for each input, in order (null where the read-only blob wrote
 * nothing).
 */
export async function addImageCards(
  mutate: DeckBlobState['mutate'],
  images: ImageCardInput[],
  opts: { reuse?: boolean } = {},
): Promise<(number | null)[]> {
  const ids: (number | null)[] = images.map(() => null);
  if (images.length === 0) return ids;
  await mutate((blob) => {
    let id = nextBlobId(blob);
    const placedSoFar: PlacementRect[] = [];
    const cards: BirtvyImage[] = [];
    images.forEach((image, i) => {
      const existing = opts.reuse
        ? [...(blob.images ?? []), ...cards].find((card) => card.src === image.src)
        : undefined;
      if (existing) {
        ids[i] = existing.id;
        return;
      }
      const { x, y } = placeIn(blob, { width: image.width, height: image.height }, placedSoFar);
      placedSoFar.push({ x, y, width: image.width, height: image.height });
      cards.push({
        id,
        x,
        y,
        width: image.width,
        height: image.height,
        src: image.src,
        // WHAT this picture is, in the words it arrived by. The row reads it as the title, and without
        // it a pool URL has no filename to fall back on.
        label: image.name,
        groupId: null,
      });
      ids[i] = id;
      id += 1;
    });
    return cards.length === 0 ? blob : { ...blob, images: [...(blob.images ?? []), ...cards] };
  });
  return ids;
}

/**
 * Upload to the conversation's image pool, then place a card per file.
 *
 * The pool is the store and the card is the placement, same split as documents. All the files land in
 * ONE `mutate` ({@link addImageCards}).
 */
async function createImageCards(
  conversationId: string,
  mutate: DeckBlobState['mutate'],
  onRefuse: (message: string) => void,
): Promise<number | null> {
  const files = await pickImageFiles();
  if (files.length === 0) return null;

  const uploaded: ImageCardInput[] = [];
  let heicRefused = false;
  for (const file of files) {
    // Transcode what the vision analysis cannot read later (AVIF, HEIC) — parity with the Library.
    const readable = await ensureVisionCompatibleImageFile(file);
    // Only Safari decodes HEIC, so elsewhere the photo comes back unchanged: refuse it rather than store a
    // picture the page cannot show and the model cannot read.
    if (isHeicFile(readable)) {
      heicRefused = true;
      continue;
    }
    const image = await uploadPoolImage(conversationId, readable);
    if (!image) continue;
    const natural = await measureImageUrl(image.url);
    const box = imageInsertSize(natural?.width ?? 0, natural?.height ?? 0);
    uploaded.push({ src: image.url, width: box.width, height: box.height, name: file.name });
  }
  if (heicRefused) onRefuse(HEIC_NOT_SUPPORTED);
  if (uploaded.length === 0) return null;

  const ids = await addImageCards(mutate, uploaded);
  return ids.find((id): id is number => id !== null) ?? null;
}

/**
 * A blank website card — a row waiting for its address.
 *
 * `status: 'empty'` is not a placeholder invented here: it is the first member of `BirtvyWeb.status`
 * and the canvas has minted cards in it since `addEmptyWeb` (`useBirtvyWebActions`), whose blank card
 * draws a URL input for a body. This is that card in this workspace, and everything downstream was
 * already waiting for it — `websiteItems` reads `empty` as a `queued` row, `stageFor` reads it as the
 * pane's `empty` status, and `rowMenus` already withholds Copy URL and Re-fetch from a card with no
 * url. Nothing new had to learn about it.
 *
 * `WEB_CARD_DEFAULT_SIZE`, not the social size, because nothing yet knows which it is. `resolve`
 * re-sizes the card to `SOCIAL_CARD_SIZE` if the address turns out to be a post — the same correction
 * `refetch` already makes when a site becomes one.
 *
 * The fetch that used to be here (the social fork, the screenshot endpoint, `error` on a throw) lives
 * in `useWebActions.resolve` now. It moved with the question it answers.
 */
/**
 * A website, fetched first and then written once.
 *
 * FETCH BEFORE MINT, and that order is the whole point (Beka, 2026-09-05). The card used to be minted
 * blank and resolved afterwards, so pressing "Add website" put an `Untitled · Queued` row in the
 * directory before anybody had said which site it was — a deck entry for a decision not yet made. The
 * field in the preview is a starting point, not a card, so nothing is written until it is submitted
 * and the page has answered.
 *
 * The consequence to accept: the wait is unattributed. There is no row to hang `updating` on, so the
 * intake holds its own pending state while this runs. That is the correct trade — a card you cannot
 * name yet is worse than a spinner on the field you are typing into.
 *
 * A page that cannot be reached STILL makes a card, in its `error` state, because the address is what
 * the user asked for and throwing it away to a failed scrape is the one outcome that wastes the typing.
 */
async function createWebsiteCard(
  conversationId: string,
  mutate: DeckBlobState['mutate'],
  entered: string,
): Promise<number | null> {
  if (!entered.trim()) return null;
  const preview = await fetchWebPreview(conversationId, entered);
  const minted: Minted = { id: null };
  await mutate((blob) => {
    const id = nextBlobId(blob);
    const { x, y } = placeIn(blob, preview.size);
    const card: BirtvyWeb = {
      id,
      x,
      y,
      width: preview.size.width,
      height: preview.size.height,
      kind: 'web',
      url: preview.url,
      status: 'ready',
      sourceImages: [],
      ...preview.patch,
    };
    minted.id = id;
    return { ...blob, webs: [...(blob.webs ?? []), card] };
  });
  return minted.id;
}

// ── The hook ─────────────────────────────────────────────────────────────────────────────────────────

export function useDeckCreate(
  conversationId: string | undefined,
  mutate: DeckBlobState['mutate'],
): DeckCreate {
  const [creating, setCreating] = useState(false);
  const client = useQueryClient();
  const { showToast } = useToast();

  const create = useCallback(
    async (item: DirectoryAddItem): Promise<string | null> => {
      if (!conversationId) return null;
      // Figma is out of scope this pass. The menu row exists because the design draws it; returning
      // null is what stops it pretending otherwise.
      if (item === 'figma') return null;

      setCreating(true);
      try {
        switch (item) {
          case 'artifact': {
            const id = await createArtifact(mutate);
            return id === null ? null : itemKey('artifacts', id);
          }
          case 'document': {
            const id = await createDocumentCard(conversationId, mutate);
            /*
             * Refetch the Library before returning.
             *
             * `useWorkspaceResources` PROJECTS the documents list over that query and drops any blob
             * card whose `documentId` is not in it — which is how a deleted document stops lingering
             * as a card, and is also why a brand-new one was invisible: the row and the card both
             * existed, the cached library did not know, and the projection filtered the card out.
             * The button looked like it had done nothing.
             */
            if (id !== null) await client.invalidateQueries({ queryKey: libraryQueryKey(conversationId) });
            return id === null ? null : itemKey('documents', id);
          }
          case 'images': {
            const id = await createImageCards(conversationId, mutate, (message) =>
              showToast({ title: message, variant: 'error' }),
            );
            return id === null ? null : itemKey('images', id);
          }
          // 'website' is NOT here. It is the one add that cannot be completed from the button —
          // it needs an address — so the host opens the preview's intake instead and comes back
          // through `createWebsite` once there is one. Falling through returns null, which is what
          // "nothing was made" already means to every caller.
          default:
            return null;
        }
      } catch (e) {
        console.warn('[deck15] create failed', item, e);
        return null;
      } finally {
        setCreating(false);
      }
    },
    [client, conversationId, mutate, showToast],
  );

  const createWebsite = useCallback(
    async (url: string): Promise<string | null> => {
      if (!conversationId) return null;
      setCreating(true);
      try {
        const id = await createWebsiteCard(conversationId, mutate, url);
        return id === null ? null : itemKey('websites', id);
      } catch (e) {
        console.warn('[deck15] create website failed', url, e);
        return null;
      } finally {
        setCreating(false);
      }
    },
    [conversationId, mutate],
  );

  return { create, createWebsite, creating };
}
